/**
 * providers/ProviderRegistry.ts — the swappable provider switchboard.
 *
 * Providers are stored per domain in priority order (lowest `priority` first).
 * The engine walks this list as its fallback chain. Registering a real
 * integration is one call; it slots ahead of the placeholder by using a lower
 * priority number. Nothing else in WorkRoamer changes.
 */

import type { FusionDomain } from '../models';
import type { FusionProvider, ProviderMeta } from './types';
import { MOCK_PROVIDERS } from './mockProviders';
import { openMeteoWeatherProvider } from './OpenMeteoWeatherProvider';

export class FusionProviderRegistry {
  private byDomain = new Map<FusionDomain, FusionProvider[]>();

  /** Add a provider. Re-sorts the domain chain by ascending priority. */
  register(provider: FusionProvider): void {
    const list = this.byDomain.get(provider.meta.domain) ?? [];
    // Replacing the same id keeps the chain clean on hot-swaps.
    const filtered = list.filter((p) => p.meta.id !== provider.meta.id);
    filtered.push(provider);
    filtered.sort((a, b) => a.meta.priority - b.meta.priority);
    this.byDomain.set(provider.meta.domain, filtered);
  }

  /** Ordered fallback chain for a domain (primary → fallback → …). */
  chain(domain: FusionDomain): FusionProvider[] {
    return this.byDomain.get(domain) ?? [];
  }

  has(domain: FusionDomain): boolean {
    return (this.byDomain.get(domain)?.length ?? 0) > 0;
  }

  /** Remove a provider by id from its domain chain. */
  unregister(domain: FusionDomain, providerId: string): void {
    const list = this.byDomain.get(domain);
    if (list) this.byDomain.set(domain, list.filter((p) => p.meta.id !== providerId));
  }

  listProviders(): ProviderMeta[] {
    return Array.from(this.byDomain.values()).flat().map((p) => ({ ...p.meta }));
  }
}

/** Build a registry with a placeholder provider registered for every domain. */
export function createDefaultRegistry(): FusionProviderRegistry {
  const registry = new FusionProviderRegistry();
  for (const provider of MOCK_PROVIDERS) registry.register(provider);
  return registry;
}

/** App-wide default registry. Real integrations register here. */
export const fusionProviderRegistry = createDefaultRegistry();

// LOCAL INTELLIGENCE™ (Build 2.04.1): the app-wide registry carries the LIVE
// Open-Meteo weather provider — it wins over the placeholder via priority,
// and the mock remains as the documented fallback-chain tail. Tests that
// build isolated registries via createDefaultRegistry() stay placeholder-only.
fusionProviderRegistry.register(openMeteoWeatherProvider);
